import { Injectable } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { EMPTY, Observable } from 'rxjs'
import { concatMap, expand, map, toArray } from 'rxjs/operators'
import { from } from 'rxjs'
import { ManagementApiBaseService } from '@services/api/management-api-base.service'
import { Paginate } from '@models/paginate.model'
import { VisitorParticipant, VisitorProcessSession } from '@models/visitor/visitor-session.model'
import { EnrollmentPublicRecord } from '@models/supplier/notice/enrollment-public-record.model'
import {
	NoticeSessionPhaseBid,
	NoticeSessionPhaseBidSearch,
	NoticeSessionPhaseBidSelectionResult,
	NoticeSessionPhaseBidTargetsResponse
} from '@models/planning/notice-session-phase-bid.model'
import { SupplierEnrollmentNoticeBid, SupplierEnrollmentNoticeBidSearch } from '@models/supplier/notice/proposal/supplier-enrollment-notice-bid.model'
import { SupplierEnrollmentNoticeProposal } from '@models/supplier/notice/proposal/supplier-enrollment-notice-proposal.model'
import { SupplierEnrollmentNoticeTrading } from '@models/supplier/notice/proposal/supplier-enrollment-notice-trading.model'
import { DemandIntentDocumentItemProposal } from '@models/demand/demand-intent-document-item-proposal.model'
import { DemandIntentDocumentEvent } from '@models/demand/demand-intent-document-event.model'
import { ChatMessage, ChatMessageSlice } from '@models/chat/chat.model'

/** Página das leituras do visitante, como o management a devolve. */
interface VisitorPage<T> {
	content: T[]
	number: number
	size: number
	totalElements?: number
	last: boolean
}

/** As leituras de fase que o painel de lances faz, do jeito que ele as chama (ver `phaseReader`). */
export interface VisitorPhaseReader {
	search(query?: NoticeSessionPhaseBidSearch, params?: HttpParams): Observable<Paginate<NoticeSessionPhaseBid>>
	targets(phaseId: string, params?: HttpParams): Observable<NoticeSessionPhaseBidTargetsResponse>
	selections(phaseId: string, params?: HttpParams): Observable<NoticeSessionPhaseBidSelectionResult[]>
}

/** As leituras de lance que o painel de lances faz (ver `bidReader`). */
export interface VisitorBidReader {
	search(query?: SupplierEnrollmentNoticeBidSearch, params?: HttpParams): Observable<Paginate<SupplierEnrollmentNoticeBid>>
	fetchAll(query?: SupplierEnrollmentNoticeBidSearch, params?: HttpParams): Observable<SupplierEnrollmentNoticeBid[]>
}

const toPaginate = <T>(page: VisitorPage<T>, content: T[]): Paginate<T> =>
	({
		content,
		page: page?.number ?? 0,
		size: page?.size ?? content.length,
		totalElements: page?.totalElements ?? content.length,
		last: page?.last ?? true,
		empty: content.length === 0,
		totalPages: Math.max(1, Math.ceil((page?.totalElements ?? content.length) / Math.max(1, page?.size ?? 1)))
	}) as Paginate<T>

/** A data de criação vem como `createdAt` nas respostas do visitante; os modelos do painel leem `created`. */
const withCreated = <T extends { created?: string; createdAt?: string }>(it: T): T => ({ ...it, created: it?.created ?? it?.createdAt })

/**
 * Leituras da sessão para o visitante (spec 048), todas sobre `/visitors/…` do management, mapeadas para os modelos
 * que os painéis do participante já usam. O `ref` opaco ocupa o lugar de `enrollment.id`/`enrollmentId`: dentro da
 * fase ele é estável, e o painel o usa só como chave.
 */
@Injectable({
	providedIn: 'root'
})
export class VisitorSessionService extends ManagementApiBaseService<any> {
	constructor(override http: HttpClient) {
		super(http)
	}

	processSession(externalCode: string): Observable<VisitorProcessSession> {
		return this.get<VisitorProcessSession>(`visitors/processes/${encodeURIComponent(externalCode)}/session`).pipe(
			map(response => new VisitorProcessSession(response))
		)
	}

	phases(sessionId: string): Observable<NoticeSessionPhaseBid[]> {
		return this.post<object, any[]>(`visitors/sessions/${sessionId}/phases/search`, {}).pipe(
			map(response => (response ?? []).map(it => new NoticeSessionPhaseBid(withCreated(it))))
		)
	}

	targets(sessionId: string, phaseId: string): Observable<NoticeSessionPhaseBidTargetsResponse> {
		return this.get<NoticeSessionPhaseBidTargetsResponse>(`visitors/sessions/${sessionId}/phases/${phaseId}/targets`).pipe(
			map(response => new NoticeSessionPhaseBidTargetsResponse(response))
		)
	}

	selections(sessionId: string, phaseId: string): Observable<NoticeSessionPhaseBidSelectionResult[]> {
		return this.get<NoticeSessionPhaseBidSelectionResult[]>(`visitors/sessions/${sessionId}/phases/${phaseId}/selections`).pipe(
			map(response => (response ?? []).map(it => new NoticeSessionPhaseBidSelectionResult(it)))
		)
	}

	rankingByPhase(
		sessionId: string,
		phaseId: string
	): Observable<{ proposals: SupplierEnrollmentNoticeProposal[]; tradings: SupplierEnrollmentNoticeTrading[] }> {
		return this.get<any>(`visitors/sessions/${sessionId}/phases/${phaseId}/ranking`).pipe(
			map(response => ({
				proposals: (response?.proposals ?? []).map((it: any) => new SupplierEnrollmentNoticeProposal(withCreated(it))),
				tradings: (response?.tradings ?? []).map((it: any) => new SupplierEnrollmentNoticeTrading(it))
			}))
		)
	}

	bids(sessionId: string, phaseId: string, page = 0, size = 100, itemIds?: string[]): Observable<Paginate<SupplierEnrollmentNoticeBid>> {
		const params = new HttpParams().set('page', page).set('size', size)

		return this.post<object, VisitorPage<any>>(`visitors/sessions/${sessionId}/bids/search`, { phaseId, itemIds }, params).pipe(
			map(response =>
				toPaginate(
					response,
					(response?.content ?? []).map(it => new SupplierEnrollmentNoticeBid(withCreated(it)))
				)
			)
		)
	}

	allBids(sessionId: string, phaseId: string): Observable<SupplierEnrollmentNoticeBid[]> {
		let page = 0

		return this.bids(sessionId, phaseId, page, 200).pipe(
			expand(response => (response.last || response.content.length === 0 ? EMPTY : this.bids(sessionId, phaseId, ++page, 200))),
			concatMap(response => from(response.content)),
			toArray()
		)
	}

	/** Dispensa sem disputa (RF 32). */
	rankingByItem(sessionId: string, itemId: string): Observable<SupplierEnrollmentNoticeProposal[]> {
		return this.get<{ proposals?: any[] }>(`visitors/sessions/${sessionId}/items/${itemId}/ranking`).pipe(
			map(response => (response?.proposals ?? []).map((it: any) => new SupplierEnrollmentNoticeProposal(withCreated(it))))
		)
	}

	/** Dispensa sem disputa (RF 32). */
	itemsProposals(sessionId: string): Observable<DemandIntentDocumentItemProposal[]> {
		return this.get<any[]>(`visitors/sessions/${sessionId}/items/proposals`).pipe(
			map(response => (response ?? []).map(it => new DemandIntentDocumentItemProposal(it)))
		)
	}

	events(sessionId: string): Observable<DemandIntentDocumentEvent[]> {
		return this.get<any[]>(`visitors/sessions/${sessionId}/events`).pipe(map(response => (response ?? []).map(it => new DemandIntentDocumentEvent(it))))
	}

	messages(sessionId: string, phaseId?: string | null, page = 0, size = 50): Observable<ChatMessageSlice> {
		let params = new HttpParams().set('page', page).set('size', size).set('sort', 'createdAt,desc')

		if (phaseId) params = params.set('phaseId', phaseId)

		return this.get<VisitorPage<any>>(`visitors/sessions/${sessionId}/messages`, params).pipe(
			map(response => ({
				content: (response?.content ?? []).map(it => new ChatMessage(it)),
				number: response?.number ?? page,
				size: response?.size ?? size,
				last: response?.last ?? true
			}))
		)
	}

	/** Participantes da disputa depois da abertura da sessão (spec 067). */
	participants(sessionId: string, page = 0, size = 20): Observable<Paginate<VisitorParticipant>> {
		const params = new HttpParams().set('page', page).set('size', size)

		return this.get<VisitorPage<any>>(`visitors/sessions/${sessionId}/participants`, params).pipe(
			map(response =>
				toPaginate(
					response,
					(response?.content ?? []).map(it => new VisitorParticipant(it))
				)
			)
		)
	}

	/** Os atos públicos do participante, pelo `ref` (spec 067). */
	participantRecord(sessionId: string, ref: string): Observable<EnrollmentPublicRecord> {
		return this.get<EnrollmentPublicRecord>(`visitors/sessions/${sessionId}/participants/${ref}/record`).pipe(
			map(response => new EnrollmentPublicRecord(response))
		)
	}

	/** Documento do participante, pelo rótulo que a leitura devolveu: a URL assinada sai do management. */
	participantAttachmentDownload(sessionId: string, ref: string, attachmentKey: string) {
		return this.get<{ preSignedUrl: string }>(`visitors/sessions/${sessionId}/participants/${ref}/attachments/${attachmentKey}/download`)
	}

	/** O painel de lances lê as fases por aqui no modo leitura, com a mesma assinatura do serviço do participante. */
	phaseReader(sessionId: string): VisitorPhaseReader {
		return {
			search: () => this.phases(sessionId).pipe(map(phases => toPaginate({ content: phases, number: 0, size: phases.length, last: true }, phases))),
			targets: phaseId => this.targets(sessionId, phaseId),
			selections: phaseId => this.selections(sessionId, phaseId)
		}
	}

	/** O painel de lances lê os lances por aqui no modo leitura; a busca é sempre numa fase da sessão. */
	bidReader(sessionId: string): VisitorBidReader {
		return {
			search: query => this.bids(sessionId, query?.phaseId, 0, 200),
			fetchAll: query => this.allBids(sessionId, query?.phaseId)
		}
	}
}
